import React, { useState, useEffect, useContext } from 'react';
import {
  InputNumber,
  Switch,
  Collapse,
  Space,
  Typography,
  Divider,
  Drawer,
} from 'antd';
import { HexColorPicker } from 'react-colorful';
import * as THREE from 'three';
import { ThemeContext } from 'pages/_app';
import { LightSettingsDrawerProps } from 'src/types';
import styles from './object-light-props.module.css';

const { Title, Text } = Typography;

export default function LightSettingsDrawer({
  selectedModel,
  isOpen,
  onClose,
}: LightSettingsDrawerProps) {
  const [color, setColor] = useState<string>('#b32aa9');
  const [groundColor, setGroundColor] = useState<string>('#b32aa9');
  const [intensity, setIntensity] = useState<number>(1);
  const [distance, setDistance] = useState<number>(0);
  const [angle, setAngle] = useState<number>(Math.PI / 3);
  const [penumbra, setPenumbra] = useState<number>(0);
  const [decay, setDecay] = useState<number>(0);
  const [castShadow, setCastShadow] = useState<boolean>(false);

  const [shadowMapWidth, setShadowMapWidth] = useState<number>(512);
  const [shadowMapHeight, setShadowMapHeight] = useState<number>(512);
  const [shadowBias, setShadowBias] = useState<number>(-0.002);

  const [shadowCameraNear, setShadowCameraNear] = useState<number>(1);
  const [shadowCameraFar, setShadowCameraFar] = useState<number>(500);
  const [shadowCameraLeft, setShadowCameraLeft] = useState<number>(-50);
  const [shadowCameraRight, setShadowCameraRight] = useState<number>(50);
  const [shadowCameraTop, setShadowCameraTop] = useState<number>(50);
  const [shadowCameraBottom, setShadowCameraBottom] = useState<number>(-50);

  const lightThreeObj = selectedModel?.props?.light?.ref?.current;
  const threeObj = selectedModel?.props?.asset?.ref?.current;
  const { isDark } = useContext(ThemeContext);

  useEffect(() => {
    if (!lightThreeObj) return;
    setColor(`#${lightThreeObj.color.getHexString()}`);
    setIntensity(lightThreeObj.intensity);
    setDistance(lightThreeObj.distance);
    setAngle(lightThreeObj.angle);
    setPenumbra(lightThreeObj.penumbra);
    setDecay(lightThreeObj.decay);
    setCastShadow(threeObj.castShadow || false);

    if (lightThreeObj.isHemisphereLight && lightThreeObj.groundColor) {
      setGroundColor(`#${lightThreeObj.groundColor.getHexString()}`);
    }

    if (lightThreeObj.shadow) {
      setShadowMapWidth(lightThreeObj.shadow.mapSize.width);
      setShadowMapHeight(lightThreeObj.shadow.mapSize.height);
      setShadowBias(lightThreeObj.shadow.bias);

      if (lightThreeObj.shadow.camera) {
        setShadowCameraNear(lightThreeObj.shadow.camera.near);
        setShadowCameraFar(lightThreeObj.shadow.camera.far);

        if (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight) {
          setShadowCameraLeft(lightThreeObj.shadow.camera.left);
          setShadowCameraRight(lightThreeObj.shadow.camera.right);
          setShadowCameraTop(lightThreeObj.shadow.camera.top);
          setShadowCameraBottom(lightThreeObj.shadow.camera.bottom);
        }
      }
    }
  }, [selectedModel, lightThreeObj, threeObj, isOpen]);

  const getLightTypeName = () => {
    let name = '';
    if (selectedModel) {
      name = selectedModel.props.asset.name.replaceAll('-', ' ');
    }
    return name;
  };

  const onChangeIntensity = (val) => {
    let valueOut = val || 0;
    setIntensity(valueOut);
    if (lightThreeObj) {
      lightThreeObj.intensity = valueOut;
    }
  };

  const onChangeDistance = (val) => {
    let valueOut = val || 0;
    setDistance(valueOut);
    if (lightThreeObj) {
      lightThreeObj.distance = valueOut;
    }
  };

  const onChangeDecay = (val) => {
    let valueOut = val || 0;
    setDecay(valueOut);
    if (lightThreeObj) {
      lightThreeObj.decay = valueOut;
    }
  };

  const onChangeAngle = (val) => {
    let valueOut = val || 0;
    setAngle(valueOut);
    if (lightThreeObj) {
      lightThreeObj.angle = valueOut;
    }
  };

  const onChangePrenumbra = (val) => {
    let valueOut = val || 0;
    setPenumbra(valueOut);
    if (lightThreeObj) {
      lightThreeObj.penumbra = valueOut;
    }
  };

  const onChangeColor = (val) => {
    setColor(val);
    if (lightThreeObj) {
      lightThreeObj.color = new THREE.Color(Number(`0x${val.slice(1)}`));
    }
  };

  const onChangeGroundColor = (val) => {
    setGroundColor(val);
    if (lightThreeObj) {
      lightThreeObj.groundColor = new THREE.Color(Number(`0x${val.slice(1)}`));
    }
  };

  const onChangeCastShadow = (checked) => {
    setCastShadow(checked);
    if (threeObj) {
      threeObj.castShadow = checked;
    }
    if (lightThreeObj) {
      lightThreeObj.castShadow = checked;

      if (checked && lightThreeObj.shadow) {
        lightThreeObj.shadow.mapSize.width = shadowMapWidth;
        lightThreeObj.shadow.mapSize.height = shadowMapHeight;

        lightThreeObj.shadow.bias = shadowBias;

        if (lightThreeObj.shadow.camera) {
          lightThreeObj.shadow.camera.near = shadowCameraNear;
          lightThreeObj.shadow.camera.far = shadowCameraFar;

          if (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight) {
            lightThreeObj.shadow.camera.left = shadowCameraLeft;
            lightThreeObj.shadow.camera.right = shadowCameraRight;
            lightThreeObj.shadow.camera.top = shadowCameraTop;
            lightThreeObj.shadow.camera.bottom = shadowCameraBottom;
            lightThreeObj.shadow.camera.updateProjectionMatrix();
          }
        }

        lightThreeObj.shadow.needsUpdate = true;
      }
    }
  };

  const onChangeShadowMapWidth = (val) => {
    const valueOut = val || 512;
    setShadowMapWidth(valueOut);
    if (lightThreeObj?.shadow) {
      lightThreeObj.shadow.mapSize.width = valueOut;
      lightThreeObj.shadow.map = null;
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowMapHeight = (val) => {
    const valueOut = val || 512;
    setShadowMapHeight(valueOut);
    if (lightThreeObj?.shadow) {
      lightThreeObj.shadow.mapSize.height = valueOut;
      lightThreeObj.shadow.map = null;
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowBias = (val) => {
    const valueOut = val || -0.002;
    setShadowBias(valueOut);
    if (lightThreeObj?.shadow) {
      lightThreeObj.shadow.bias = valueOut;
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraNear = (val) => {
    const valueOut = val || 1;
    setShadowCameraNear(valueOut);
    if (lightThreeObj?.shadow?.camera) {
      lightThreeObj.shadow.camera.near = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraFar = (val) => {
    const valueOut = val || 500;
    setShadowCameraFar(valueOut);
    if (lightThreeObj?.shadow?.camera) {
      lightThreeObj.shadow.camera.far = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraLeft = (val) => {
    const valueOut = val || -5;
    setShadowCameraLeft(valueOut);
    if (
      lightThreeObj?.shadow?.camera &&
      (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight)
    ) {
      lightThreeObj.shadow.camera.left = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraRight = (val) => {
    const valueOut = val || 5;
    setShadowCameraRight(valueOut);
    if (
      lightThreeObj?.shadow?.camera &&
      (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight)
    ) {
      lightThreeObj.shadow.camera.right = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraTop = (val) => {
    const valueOut = val || 5;
    setShadowCameraTop(valueOut);
    if (
      lightThreeObj?.shadow?.camera &&
      (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight)
    ) {
      lightThreeObj.shadow.camera.top = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  const onChangeShadowCameraBottom = (val) => {
    const valueOut = val || -5;
    setShadowCameraBottom(valueOut);
    if (
      lightThreeObj?.shadow?.camera &&
      (lightThreeObj.isDirectionalLight || lightThreeObj.isSpotLight)
    ) {
      lightThreeObj.shadow.camera.bottom = valueOut;
      lightThreeObj.shadow.camera.updateProjectionMatrix();
      lightThreeObj.shadow.needsUpdate = true;
    }
  };

  if (!selectedModel || !lightThreeObj) {
    return null;
  }

  return (
    <Drawer
      title={getLightTypeName()}
      placement='right'
      width={400}
      onClose={onClose}
      open={isOpen}
      destroyOnClose
    >
      <Space direction='vertical' size='large' className={styles.container}>
        <div>
          <Text strong>Intensity</Text>
          <InputNumber
            size='small'
            min={0}
            step={0.1}
            value={intensity}
            onChange={onChangeIntensity}
            className={styles.inputNumber}
          />
        </div>

        {(lightThreeObj.isSpotLight || lightThreeObj.isPointLight) && (
          <div>
            <Space
              direction='vertical'
              size='small'
              className={styles.inputGroup}
            >
              <div>
                <Text strong>Distance</Text>
                <InputNumber
                  size='small'
                  min={0}
                  step={0.1}
                  value={distance}
                  onChange={onChangeDistance}
                  className={styles.inputNumber}
                />
              </div>
              <div>
                <Text strong>Decay</Text>
                <InputNumber
                  size='small'
                  value={decay}
                  onChange={onChangeDecay}
                  min={0}
                  max={2}
                  step={0.1}
                  className={styles.inputNumber}
                />
              </div>
            </Space>
          </div>
        )}

        {lightThreeObj.isSpotLight && (
          <div>
            <Space
              direction='vertical'
              size='small'
              className={styles.inputGroup}
            >
              <div>
                <Text strong>Angle</Text>
                <InputNumber
                  size='small'
                  value={angle}
                  min={0}
                  max={parseFloat((Math.PI / 2).toFixed(2))}
                  step={(Math.PI / 12).toFixed(2)}
                  onChange={onChangeAngle}
                  className={styles.inputNumber}
                />
              </div>
              <div>
                <Text strong>Penumbra</Text>
                <InputNumber
                  size='small'
                  value={penumbra}
                  min={0}
                  max={1}
                  step={0.1}
                  onChange={onChangePrenumbra}
                  className={styles.inputNumber}
                />
              </div>
            </Space>
          </div>
        )}

        {!lightThreeObj.isAmbientLight && (
          <div>
            <Space className={styles.castShadowContainer}>
              <Text strong>Cast Shadow</Text>
              <Switch checked={castShadow} onChange={onChangeCastShadow} />
            </Space>
          </div>
        )}

        {castShadow && (
          <div>
            <Divider />
            <Collapse>
              <Collapse.Panel header='Shadow Properties' key='1'>
                <Space
                  direction='vertical'
                  size='large'
                  className={styles.shadowContent}
                >
                  <div>
                    <Text strong>Shadow Map Resolution</Text>
                    <Space className={styles.inputRow}>
                      <div className={styles.inputContainer}>
                        <Text type='secondary' className={styles.inputLabel}>
                          Width
                        </Text>
                        <InputNumber
                          size='small'
                          min={128}
                          max={4096}
                          step={128}
                          value={shadowMapWidth}
                          onChange={onChangeShadowMapWidth}
                          className={styles.inputNumberSmall}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <Text type='secondary' className={styles.inputLabel}>
                          Height
                        </Text>
                        <InputNumber
                          size='small'
                          min={128}
                          max={4096}
                          step={128}
                          value={shadowMapHeight}
                          onChange={onChangeShadowMapHeight}
                          className={styles.inputNumberSmall}
                        />
                      </div>
                    </Space>
                  </div>

                  <div>
                    <Text strong>Shadow Bias</Text>
                    <InputNumber
                      size='small'
                      min={-0.1}
                      max={0.1}
                      step={0.0001}
                      value={shadowBias}
                      onChange={onChangeShadowBias}
                      className={styles.inputNumber}
                    />
                  </div>
                </Space>
              </Collapse.Panel>

              <Collapse.Panel header='Shadow Camera' key='2'>
                <Space
                  direction='vertical'
                  size='large'
                  className={styles.shadowCameraContent}
                >
                  <div>
                    <Text strong>Camera Settings</Text>
                    <Space className={styles.cameraSettingsRow}>
                      <div className={styles.inputContainer}>
                        <Text type='secondary' className={styles.inputLabel}>
                          Near
                        </Text>
                        <InputNumber
                          size='small'
                          min={0.1}
                          max={100}
                          step={0.1}
                          value={shadowCameraNear}
                          onChange={onChangeShadowCameraNear}
                          className={styles.inputNumberSmall}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <Text type='secondary' className={styles.inputLabel}>
                          Far
                        </Text>
                        <InputNumber
                          size='small'
                          min={1}
                          max={5000}
                          step={1}
                          value={shadowCameraFar}
                          onChange={onChangeShadowCameraFar}
                          className={styles.inputNumberSmall}
                        />
                      </div>
                    </Space>
                  </div>

                  {(lightThreeObj.isDirectionalLight ||
                    lightThreeObj.isSpotLight) && (
                    <>
                      <div>
                        <Text strong>Camera Bounds</Text>
                        <Space className={styles.cameraBoundsRow}>
                          <div className={styles.inputContainer}>
                            <Text
                              type='secondary'
                              className={styles.inputLabel}
                            >
                              Left
                            </Text>
                            <InputNumber
                              size='small'
                              min={-1000}
                              max={0}
                              step={0.5}
                              value={shadowCameraLeft}
                              onChange={onChangeShadowCameraLeft}
                              className={styles.inputNumberSmall}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <Text
                              type='secondary'
                              className={styles.inputLabel}
                            >
                              Right
                            </Text>
                            <InputNumber
                              size='small'
                              min={0}
                              max={1000}
                              step={0.5}
                              value={shadowCameraRight}
                              onChange={onChangeShadowCameraRight}
                              className={styles.inputNumberSmall}
                            />
                          </div>
                        </Space>
                      </div>

                      <div>
                        <Space className={styles.cameraBoundsRowSmall}>
                          <div className={styles.inputContainer}>
                            <Text
                              type='secondary'
                              className={styles.inputLabel}
                            >
                              Top
                            </Text>
                            <InputNumber
                              size='small'
                              min={0}
                              max={1000}
                              step={0.5}
                              value={shadowCameraTop}
                              onChange={onChangeShadowCameraTop}
                              className={styles.inputNumberSmall}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <Text
                              type='secondary'
                              className={styles.inputLabel}
                            >
                              Bottom
                            </Text>
                            <InputNumber
                              size='small'
                              min={-1000}
                              max={0}
                              step={0.5}
                              value={shadowCameraBottom}
                              onChange={onChangeShadowCameraBottom}
                              className={styles.inputNumberSmall}
                            />
                          </div>
                        </Space>
                      </div>
                    </>
                  )}
                </Space>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        <div>
          <Text strong>Light Color</Text>
          <div className={styles.colorPickerContainer}>
            <HexColorPicker color={color} onChange={onChangeColor} />
          </div>
        </div>
        {lightThreeObj.isHemisphereLight && (
          <div>
            <Text strong>Ground Color</Text>
            <div className={styles.colorPickerContainer}>
              <HexColorPicker
                color={groundColor}
                onChange={onChangeGroundColor}
              />
            </div>
          </div>
        )}
      </Space>
    </Drawer>
  );
}
